<template>
  <div v-if="!isLoading" class="flex h-full flex-col p-8 pt-6">
    <data-orchestration-bar v-if="showDoBar" class="mb-6">
      <data-orchestration-tabs-services
        :active-tab="activeTab"
        @show-service="showService"
        @show-tab="(tabName: string) => selectTab(tabName)"
      />
    </data-orchestration-bar>
    <template v-if="stageGroup === STAGE_GROUP_TYPE.WITH_BLOCKS">
      <div class="flex items-baseline justify-between space-x-8">
        <div v-if="!showDoBar" class="flex overflow-hidden w-full">
          <lf-scrollable-menu :scroll-length="250">
            <template v-for="(tabName, index) in tabs" :key="tabName">
              <lf-tab
                :active="tabName === activeTab?.name"
                :data-cy="tabName"
                :is-loading="tabsLoadingState[tabName]"
                class="tab-wrapper"
                @click="selectTab(tabName)"
              >
                <template v-if="isApplicationStage" #icon>
                  <div v-if="isTabWithAnyData(index)" class="h-3 w-3">
                    <icon-base
                      class="mt-2 text-checkmark-green"
                      height="12"
                      width="12"
                      :icon="IconSuccessRound"
                    />
                  </div>
                </template>
                <span :class="{ 'mt-0-5': !isTabWithAnyData(index) }">
                  {{ tabName }}
                </span>
              </lf-tab>
            </template>
          </lf-scrollable-menu>
        </div>
        <copy-deal-data-button v-show="activeStage?.type === 'application'" />
      </div>
      <div ref="services">
        <loader :is-loading="tabsLoadingState[activeTab?.name ?? '']" />
        <funders-filter
          v-if="isFundedStage && funders.length"
          :active-filter="activeFundersFilter"
          :funders="funders"
          class="mt-4"
          @update:active-filter="($event) => (activeFundersFilter = $event)"
        />
        <div v-for="block in activeTab?.blocks" :key="block.id" class="mt-5">
          <component
            :is="getWorkflowComponent(block.id as WorkflowBlockComponentsKey)"
            v-bind="componentDataMap[block.id]"
          />
        </div>
      </div>
    </template>
    <template v-else-if="isScorecardsStage">
      <scorecards-view :is-view="false" />
    </template>
    <template v-else-if="isPlacementStage">
      <Placement />
    </template>
    <template v-else-if="isOfferStage">
      <offers-block />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import { NotableType, NoteType } from "@/enums/dealActivity";
import { getEnumKeyFromValue } from "@/helpers/common";
import { PHASE_OFFER, WORKFLOW_STAGES } from "@/helpers/constants";
import {
  STAGE_GROUP_TYPE,
  WORKFLOW_BLOCKS_IDS,
} from "@/helpers/constants/workflow";
import { hasBankConnection } from "@/helpers/deals";
import { getWorkflowComponent } from "@/helpers/workflow";
import { useDealOwners, useDeals } from "@/hooks/deals";
import { useServices } from "@/hooks/services";
import {
  useActiveStage,
  useActiveTab,
  useActiveWorkflowTemplateStages,
  useWorkflowFields,
  useWorkflowStagesInfo,
  useWorkflowTabs,
} from "@/hooks/workflow";
import useApplicationsStore from "@/stores/applications";
import useNotesStore from "@/stores/notes";
import { useJsonTreeStateStore } from "@/stores/serviceJsonViewer";

import IconSuccessRound from "@/components/icons/IconSuccessRound.vue";
import DataOrchestrationBar from "@/components/deals/dataOrchestration/DataOrchestrationBar.vue";
import DataOrchestrationTabsServices from "@/components/deals/dataOrchestration/services/DataOrchestrationTabsServices.vue";
import Placement from "@/components/deals/placement/Placement.vue";
import FundersFilter, {
  type FilterData,
} from "@/components/deals/workflow/OfferStage/WorkflowLive/FundersFilter.vue";
import OffersBlock from "@/components/deals/workflow/OfferStage/WorkflowLive/Offers.vue";
import LfScrollableMenu from "@/components/ui/LfScrollableMenu.vue";
import CopyDealDataButton from "@/components/ui/buttons/CopyDealDataButton.vue";
import ScorecardsView from "@/views/profile/ScorecardsView.vue";

import type { WorkflowBlockComponentsKey } from "@/helpers/constants/WorkflowComponentMap";
import type { WorkflowBlockIds, WorkflowBlocksData } from "@/models/workflows";

const BANK_DATA_BLOCKS: Partial<WorkflowBlockIds>[] = [
  WORKFLOW_BLOCKS_IDS.bank_data_plaid,
  WORKFLOW_BLOCKS_IDS.bank_data_moneythumb,
];

const {
  activeStage,
  stageGroup,
  isScorecardsStage,
  isApplicationStage,
  isPlacementStage,
  isOfferStage,
  isFundedStage,
} = useActiveStage();
const { firstUnderwritingStageId } = useActiveWorkflowTemplateStages();
const { activeTab } = useActiveTab();
const { commit, getters, dispatch } = useStore();
const { activeDeal, approvedOffers } = useDeals();
const { getCommercialDataForProviders } = useServices();
const workflowStagesInfo = useWorkflowStagesInfo();
const { resetServiceJsonStatus } = useJsonTreeStateStore();
const { tabsLoadingState, getApplicationStageData, fetchFields } =
  useWorkflowTabs();
const allOwners = useDealOwners();

const fetchFieldActions = {
  [WORKFLOW_STAGES.APPLICATION]: getApplicationStageData,
  [WORKFLOW_STAGES.UNDERWRITING]: () =>
    fetchFields([
      WORKFLOW_BLOCKS_IDS.funding,
      WORKFLOW_BLOCKS_IDS.additinal_owner,
    ]),
  [WORKFLOW_STAGES.PLACEMENT]: () =>
    fetchFields([WORKFLOW_BLOCKS_IDS.additional_business_info]),
};

const tabs = computed(
  () => activeStage.value?.tabs?.map((tab) => tab.name) || []
);

const { fundedOffers, offers } = useDeals();

const activeFundersFilter = ref<FilterData>({
  funder_id: undefined,
  product: undefined,
});

const funders = computed(() => {
  const allFunders = fundedOffers.value.map((offer) => ({
    id: offer.funder?.id,
    full_name: offer.funder?.full_name,
    icon: offer.funder?.logo,
    product: offer.details.offer_template.id,
    product_name: offer.details.offer_template.name,
  }));

  const uniqueFunders = [];
  const seen = new Set();

  for (const funder of allFunders) {
    const uniqueKey = `${funder.full_name}-${funder.product}`;
    if (!seen.has(uniqueKey)) {
      seen.add(uniqueKey);
      uniqueFunders.push(funder);
    }
  }

  return uniqueFunders;
});

const services = ref<HTMLDivElement | null>(null);

const applicationsStore = useApplicationsStore();

const showDoBar = computed(() => activeStage.value?.type === "underwriting");

const isLoading = computed(() => getters["workflows/loading"]);

const containsBankDataBlocks = computed(() =>
  Boolean(
    activeTab.value?.blocks?.some((block) =>
      BANK_DATA_BLOCKS.includes(block.id)
    )
  )
);

const componentDataMap = computed(() => {
  const data = {
    [WORKFLOW_BLOCKS_IDS.funding_details]: {
      activeFilter: activeFundersFilter.value,
    },
    [WORKFLOW_BLOCKS_IDS.funding_summary]: {
      activeFilter: activeFundersFilter.value,
    },
    [WORKFLOW_BLOCKS_IDS.offer_details]: {
      activeFilter: activeFundersFilter.value,
    },
    [WORKFLOW_BLOCKS_IDS.insurance]: { deal: activeDeal.value },
    [WORKFLOW_BLOCKS_IDS.business_notes]: { fetchNotes: false },
    [WORKFLOW_BLOCKS_IDS.stips]: { showTypes: true },
    [WORKFLOW_BLOCKS_IDS.business_debt_schedule]: { isBusiness: true },
    [WORKFLOW_BLOCKS_IDS.personal_debt_schedule]: { isBusiness: false },
  };
  return data as Record<WorkflowBlockIds, (typeof data)[keyof typeof data]>;
});

const showService = async (tabName: string, blockId: string) => {
  selectTab(tabName);
  await nextTick();
  scrollServiceIntoView(blockId);
};

const selectTab = async (tabName: string) => {
  const newActiveTab = activeStage.value?.tabs?.find(
    (tab) => tab.name === tabName
  );
  if (tabName !== activeTab.value?.name) {
    resetServiceJsonStatus();
  }
  commit("workflows/setActiveTab", newActiveTab);
};

const isTabWithAnyData = (index: number) => {
  const blocksByTab = activeStage.value?.tabs?.[index].blocks;
  const { currentNotes } = useNotesStore();
  const activeBusinessNotes = currentNotes.data?.filter(
    (note) =>
      note.notable_type ===
        getEnumKeyFromValue(NotableType, NoteType.Business) &&
      Number(note.notable_id) === activeDeal.value.business.id
  );
  const notEmptyBlock = blocksByTab?.find((block) => {
    const { fieldData } = useWorkflowFields(
      block.id as keyof WorkflowBlocksData
    );

    switch (block.id) {
      case WORKFLOW_BLOCKS_IDS.signed_application:
        return (
          allOwners.value.length &&
          allOwners.value.every(
            (owner) =>
              owner.signed_docs?.filter((doc) => doc.signature_status === 2)
                ?.length
          )
        );
      case WORKFLOW_BLOCKS_IDS.bank_data:
        return hasBankConnection(activeDeal.value);
      case WORKFLOW_BLOCKS_IDS.discovery_questions:
        return activeDeal.value.is_discovery_complete;
      case WORKFLOW_BLOCKS_IDS.business_notes:
        return !!activeBusinessNotes.length;
      case WORKFLOW_BLOCKS_IDS.custom_attributes:
        return activeDeal.value.has_attributes;
      default:
        return !!block.fields?.find(
          (field) =>
            fieldData.value?.[field.id as keyof typeof fieldData.value] !== null
        );
    }
  });

  return !!notEmptyBlock;
};

const scrollServiceIntoView = (tabId: string) => {
  const service = services.value?.querySelector(`#${tabId}`);

  if (!service) {
    return;
  }

  service.scrollIntoView({
    block: "start",
    inline: "nearest",
    behavior: "smooth",
  });
};

watch(
  [activeStage, containsBankDataBlocks],
  ([newActiveStage]) => {
    if (
      (activeStage.value?.type === "underwriting" ||
        containsBankDataBlocks.value) &&
      !activeDeal.value.commercialData
    ) {
      dispatch("applications/getServicesOptions");
    }
    if (newActiveStage?.type === "underwriting" && activeDeal.value?.id) {
      const uwBlocks =
        workflowStagesInfo.value.find(({ type }) => type === "underwriting")
          ?.blocks ?? [];
      const blockIdsInStage = new Set(
        newActiveStage.tabs?.flatMap(
          ({ blocks }) => blocks?.map(({ id }) => id) ?? []
        )
      );

      const serviceIds = uwBlocks.reduce<string[]>((acc, block) => {
        if (blockIdsInStage.has(block.id) && block.related_services) {
          acc.push(...block.related_services);
        }
        return acc;
      }, []);

      if (serviceIds.length) {
        getCommercialDataForProviders([...new Set(serviceIds)]);
      }
    }
    if (newActiveStage?.type === "underwriting") {
      applicationsStore.getLatestDataOrchestrationLog({
        appId: activeDeal.value.id,
        stageId: newActiveStage.id,
        firstUnderwritingStageId: firstUnderwritingStageId.value,
      });
    }
    if (newActiveStage?.type && newActiveStage?.type in fetchFieldActions) {
      fetchFieldActions[
        newActiveStage.type as keyof typeof fetchFieldActions
      ]();
    }
    if (
      newActiveStage?.type === PHASE_OFFER.toLowerCase() &&
      !offers.value.length
    ) {
      dispatch("applications/getOffers");
    }
    if (
      newActiveStage?.type === WORKFLOW_STAGES.APPLICATION &&
      newActiveStage.tabs?.some((tab) =>
        tab.blocks.some((block) => block.id === WORKFLOW_BLOCKS_IDS.bank_data)
      ) &&
      !getters["applications/plaidData"]
    ) {
      dispatch("applications/getPlaidData", activeDeal.value.id);
    }
  },
  { immediate: true }
);

onMounted(() => {
  activeFundersFilter.value = {
    funder_id: funders.value[0]?.id,
    product: funders.value[0]?.product,
  };
});
watch(
  approvedOffers,
  () => {
    activeFundersFilter.value = {
      funder_id: funders.value[0]?.id,
      product: funders.value[0]?.product,
    };
  },
  { immediate: true }
);
</script>
<style scoped>
@reference "@/assets/tailwind.css";

:deep(.tab-wrapper .tab) {
  @apply flex h-full items-baseline space-x-2 text-left;
}

:deep(.tab-wrapper > div) {
  @apply h-full;
}
</style>
